/**
 * BrowserRunBlock — Admin card that runs real Playwright user journeys
 * from the Golden Path Catalogue and shows pass/fail per flow.
 * Session 269 (Feb 2026). Replaces the earlier Visual Regression block.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CheckCircle, AlertTriangle, PlayCircle, MousePointerClick, RefreshCw, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { getToken } from '@/utils/tokenManager';
import BrowserRunScreenshotDialog from './BrowserRunScreenshotDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API_URL}/api/admin/code-health/golden-paths/browser-run`;

const TARGETS = [{ value: 'preview', label: 'Preview' }, { value: 'production', label: 'Production' }];
const SUITES = [
  { value: 'safe', label: 'Safe (40)', desc: '40 read-only journeys — no data written' },
  { value: 'destructive', label: 'Destructive (28)', desc: '28 CRUD journeys against a throwaway scratch account' },
  { value: 'all', label: 'All (68)', desc: 'Every journey — takes ~10 minutes' },
];

const SUITE_DURATIONS_MS = { safe: 240000, destructive: 180000, all: 600000 };

const formatTs = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const BrowserRunBlock = () => {
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [target, setTarget] = useState('preview');
  const [suite, setSuite] = useState('safe');
  const [screenshotFor, setScreenshotFor] = useState(null);  // { flowId, error } or null
  const [history, setHistory] = useState([]);
  const timerRef = useRef(null);

  const fetchLatest = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/latest`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const body = await res.json();
      setReport(body.report || null);
    } catch (_e) { /* ignore */ }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${BASE}/history?days=14`, { headers: { Authorization: `Bearer ${getToken()}` } });
      const body = await res.json();
      setHistory(body.items || []);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchLatest();
    fetchHistory();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [fetchLatest, fetchHistory]);

  const startRun = async () => {
    setRunning(true);
    try {
      const res = await fetch(`${BASE}/run?target=${target}&suite=${suite}`, {
        method: 'POST', headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (res.status === 409) { toast.error('A browser-run is already in progress.'); return; }
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      const mins = Math.round((SUITE_DURATIONS_MS[suite] || 240000) / 60000);
      toast.success(`Browser-run started (${suite} suite, ${target}) — takes ~${mins} minute${mins === 1 ? '' : 's'}.`);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(fetchLatest, SUITE_DURATIONS_MS[suite] || 240000);
    } catch (e) { toast.error(e.message); } finally { setRunning(false); }
  };

  const allGreen = report && report.failed === 0;
  const sorted = report ? [...report.results].sort((a, b) => {
    if (a.passed !== b.passed) return a.passed ? 1 : -1;
    return (b.elapsed_ms || 0) - (a.elapsed_ms || 0);
  }) : [];

  return (
    <Card className="bg-gray-900 border-gray-800" data-testid="browser-run-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base text-white flex items-center justify-between">
          <span className="flex items-center gap-2">
            <MousePointerClick className="w-5 h-5 text-emerald-400" />
            Browser-run pipeline
          </span>
          {report && (
            <Badge className={allGreen ? 'bg-green-500 text-white' : 'bg-red-500 text-white'} data-testid="br-summary-badge">
              {report.passed}/{report.total} flows
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-gray-400">
          Real Playwright browser physically clicks and types through every Golden Path flow, then verifies the end-state.
          Catches broken clicks, wrong routing, focus bugs, and layout regressions — the class of bugs pure API probes miss.
        </p>

        {history.length > 0 && (
          <div className="flex items-center gap-2" data-testid="br-daily-strip">
            <span className="text-[11px] text-gray-500 uppercase tracking-wide whitespace-nowrap">14 days</span>
            <div className="flex gap-0.5 flex-1 min-w-0">
              {history.slice(-14).map(h => (
                <span
                  key={h.ran_at}
                  data-testid={`br-strip-dot-${(h.failed || 0) === 0 ? 'green' : 'red'}`}
                  title={`${new Date(h.ran_at).toLocaleDateString()} · ${h.passed}/${h.total}${(h.failing_flow_ids || []).length ? ' — red: ' + h.failing_flow_ids.join(', ') : ''}`}
                  className={`inline-block w-3 h-3 rounded-sm ${(h.failed || 0) === 0 ? 'bg-emerald-500' : 'bg-red-500'}`}
                />
              ))}
            </div>
            <span className="text-[11px] text-gray-500 whitespace-nowrap" data-testid="br-strip-summary">
              {history.filter(h => (h.failed || 0) === 0).length}/{history.length} green
            </span>
          </div>
        )}

        <div className="flex items-center gap-2" data-testid="br-target-toggle">
          <span className="text-[11px] text-gray-500 uppercase tracking-wide">Target</span>
          {TARGETS.map(t => (
            <button key={t.value} type="button" onClick={() => setTarget(t.value)}
              data-testid={`br-target-${t.value}`}
              className={`px-2.5 py-1 rounded text-xs font-medium border transition ${
                target === t.value
                  ? (t.value === 'production' ? 'bg-red-500/20 text-red-300 border-red-500/50' : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50')
                  : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600'
              }`}>{t.label}</button>
          ))}
        </div>

        <div className="flex items-center gap-2 flex-wrap" data-testid="br-suite-toggle">
          <span className="text-[11px] text-gray-500 uppercase tracking-wide">Suite</span>
          {SUITES.map(s => (
            <button key={s.value} type="button" onClick={() => setSuite(s.value)}
              data-testid={`br-suite-${s.value}`}
              title={s.desc}
              className={`px-2.5 py-1 rounded text-xs font-medium border transition ${
                suite === s.value
                  ? (s.value === 'destructive' ? 'bg-amber-500/20 text-amber-300 border-amber-500/50' : (s.value === 'all' ? 'bg-blue-500/20 text-blue-300 border-blue-500/50' : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50'))
                  : 'bg-gray-800 text-gray-400 border-gray-700 hover:border-gray-600'
              }`}>{s.label}</button>
          ))}
        </div>
        {suite !== 'safe' && (
          <p className="text-[11px] text-amber-300/80" data-testid="br-suite-warn">
            {suite === 'destructive' ? 'Destructive journeys create, edit, and delete real records against the throwaway account browser.scratch@example.com — never Sophia or Ope.' : 'All journeys run — safe ones first, then destructive against the throwaway account.'}
          </p>
        )}

        {report && (
          <div className="border border-gray-800 rounded-lg p-3 bg-gray-800/40" data-testid="br-last-run">
            <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
              <span>Last run: <span className="text-gray-200">{formatTs(report.ran_at)}</span> · target <span className={`font-medium ${report.target?.includes('beta.myinvestfolio.io') ? 'text-red-300' : 'text-emerald-300'}`}>{report.target?.includes('beta.myinvestfolio.io') ? 'production' : 'preview'}</span>{report.suite && <> · suite <span className={`font-medium ${report.suite === 'destructive' ? 'text-amber-300' : (report.suite === 'all' ? 'text-blue-300' : 'text-emerald-300')}`} data-testid="br-last-suite">{report.suite}</span></>}</span>
              <span className="flex items-center gap-2">
                {report.retried_pass > 0 && (
                  <span className="flex items-center gap-1 text-amber-300" data-testid="br-retried-pass" title="Flows that failed on their first try but passed on retry — worth watching">
                    <RefreshCw className="w-3 h-3" />
                    {report.retried_pass} retried
                  </span>
                )}
                <span>{report.passed}/{report.total} OK</span>
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1" data-testid="br-results">
              {sorted.map(r => (
                <div key={r.flow_id} className={`flex items-center gap-2 text-[11px] px-2 py-1 rounded ${r.passed ? (r.attempts > 1 ? 'text-amber-200 bg-amber-500/10 ring-1 ring-amber-500/40' : 'text-emerald-300 bg-emerald-500/10') : 'text-red-300 bg-red-500/10 ring-1 ring-red-500/40'}`}>
                  {r.passed ? (r.attempts > 1 ? <RefreshCw className="w-3.5 h-3.5" /> : <CheckCircle className="w-3.5 h-3.5" />) : <AlertTriangle className="w-3.5 h-3.5" />}
                  <span className="truncate flex-1" title={r.passed ? (r.attempts > 1 ? `${r.flow_id} — passed on retry` : r.flow_id) : (r.error || r.flow_id)}>{r.flow_id}</span>
                  {!r.passed && r.screenshot && (
                    <button
                      type="button"
                      onClick={() => setScreenshotFor({ flowId: r.flow_id, error: r.error })}
                      className="flex items-center gap-1 text-red-200 hover:text-white underline underline-offset-2"
                      data-testid={`br-view-screenshot-${r.flow_id}`}
                      title="See what the browser was looking at when the journey tripped"
                    >
                      <ImageIcon className="w-3 h-3" />
                      screenshot
                    </button>
                  )}
                  <span className={r.passed ? (r.attempts > 1 ? 'font-semibold' : 'text-gray-500') : 'font-semibold'}>
                    {r.passed ? (r.attempts > 1 ? 'retry' : `${r.elapsed_ms}ms`) : `step ${r.failed_step_index}`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={startRun} disabled={running} data-testid="br-run-btn"
            className={target === 'production' ? 'bg-red-600 hover:bg-red-700 text-white' : (suite === 'destructive' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white')}>
            <PlayCircle className={`w-4 h-4 mr-1.5 ${running ? 'animate-pulse' : ''}`} />
            {running ? 'Starting…' : `Run ${suite === 'safe' ? 'Safe' : suite === 'destructive' ? 'Destructive' : 'All'} Journeys (${target})`}
          </Button>
        </div>
      </CardContent>
      <BrowserRunScreenshotDialog
        open={!!screenshotFor}
        flowId={screenshotFor?.flowId}
        error={screenshotFor?.error}
        onClose={() => setScreenshotFor(null)}
      />
    </Card>
  );
};

export default BrowserRunBlock;
